import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, trackAuth } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; data: { user: User | null } | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null; data: { user: User | null } | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Register listener FIRST so we don't miss any auth events.
    // Do NOT flip loading here — that's owned by getSession() below so the
    // persisted session is always read before route guards run.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      trackAuth(`Auth state changed: ${event}`, {
        event,
        has_session: Boolean(session),
        user_id: session?.user?.id ?? null,
      });



      // Record teammate sign-in linkage for audit trail (fire-and-forget).
      // Runs on SIGNED_IN (fresh login) and TOKEN_REFRESHED (returning session);
      // the RPC dedupes within a 10-minute window server-side.
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
        setTimeout(() => {
          void supabase.rpc('record_teammate_signin').then(() => {
            /* non-fatal — audit only */
          });
        }, 0);
      }
    });

    // THEN restore the persisted session. Only this flips loading=false,
    // guaranteeing guards never see a transient !user state on refresh.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setSentryUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      trackAuth('Persisted session restored', {
        has_session: Boolean(session),
        user_id: session?.user?.id ?? null,
      });
      setLoading(false);

    });


    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    trackAuth('Sign-in attempted');
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    trackAuth(
      error ? 'Sign-in failed' : 'Sign-in succeeded',
      { error_message: error?.message },
      error ? 'error' : 'info',
    );
    return { error, data: data ? { user: data.user } : null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    trackAuth('Sign-up attempted');
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName }
      }
    });
    trackAuth(
      error ? 'Sign-up failed' : 'Sign-up succeeded',
      { error_message: error?.message },
      error ? 'error' : 'info',
    );
    return { error, data: data ? { user: data.user } : null };
  };

  const signOut = async () => {
    trackAuth('Sign-out requested');
    await supabase.auth.signOut();
  };


  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
